import { useInfiniteQuery, useQuery } from "react-query";
import reviewService from "services/review";
import useModal from "hooks/useModal";
import dynamic from "next/dynamic";
import { useAuth } from "contexts/auth/auth.context";
import PrimaryButton from "components/button/primaryButton";
import Message2FillIcon from "remixicon-react/Message2FillIcon";
import { useTranslation } from "react-i18next";
import LoadMore from "components/button/load-more";
import cls from "./productReview.module.scss";
import ProductComments from "./productComments";
import ProductSummary from "./productSummary";
import { IShop } from "../../interfaces";
import ShopMiniCard from "../shopCard/shopMiniCard";

const DrawerContainer = dynamic(() => import("containers/drawer/drawer"));
const Reviews = dynamic(() => import("components/review/review"));

type Props = {
  productId: number;
  productUuid: string;
  shop?: IShop;
};

export default function ProductReview({ productId, productUuid, shop }: Props) {
  const { t } = useTranslation();
  const [open, handleOpen, handleClose] = useModal();
  const { isAuthenticated } = useAuth();

  const {
    data: reviews,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["reviews", "products", productUuid],
    queryFn: ({ pageParam = 1 }) =>
      reviewService.getAll(productUuid, "products", {
        column: "user",
        page: pageParam,
        perPage: 3,
      }),
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    staleTime: 0,
    enabled: !!productUuid,
  });
  const reviewList =
    reviews?.pages?.flatMap((item) => item?.data)?.filter(Boolean) || [];

  const { data: summary } = useQuery({
    queryKey: ["summary", "products", productId],
    queryFn: () => reviewService.getSummary(productId, "products"),
    staleTime: 0,
    enabled: !!productId,
  });

  const {
    isLoading: isCheckCanReviewLoading,
    isError,
    data,
  } = useQuery({
    queryKey: ["canReview", "products", productId],
    queryFn: () =>
      reviewService.canReview({ type: "product", type_id: productId }),
    staleTime: 0,
    enabled: !!productId && isAuthenticated,
  });

  const isAbleToAddReview = !(
    !isAuthenticated ||
    isError ||
    isCheckCanReviewLoading ||
    !data?.data?.ordered ||
    !!data?.data?.added_review
  );

  return (
    <>
      <section className={cls.container}>
        <div className={cls.comments}>
          <ProductComments data={reviewList} handleOpenReviews={handleOpen} />
          {hasNextPage && (
            <LoadMore loading={isFetchingNextPage}>{t("load.more")}</LoadMore>
          )}
          {isAbleToAddReview && (
            <div>
              <PrimaryButton
                icon={<Message2FillIcon size={24} />}
                fullWidth={false}
                size="small"
                onClick={handleOpen}
              >
                {t("add.comment")}
              </PrimaryButton>
            </div>
          )}
        </div>
        <div className={cls.summary}>
          {/* @ts-ignore */}
          <ProductSummary data={summary} />
          {!!shop && <ShopMiniCard data={shop} label={t("product.store")} />}
        </div>
      </section>
      <DrawerContainer
        open={open}
        onClose={handleClose}
        anchor="right"
        PaperProps={{ style: { padding: 0 } }}
      >
        <Reviews
          type="products"
          typeId={productId || 0}
          typeUuid={productUuid}
        />
      </DrawerContainer>
    </>
  );
}
